import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Service } from '@/types/service';

interface UseServicesOptions {
  enabled?: boolean;
  categoryId?: string;
  search?: string;
}

export function useServices(options: UseServicesOptions = {}) {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { enabled = true, categoryId, search } = options;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    loadServices();
  }, [enabled, categoryId, search]);

  const loadServices = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get current user's company
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      // profiles.id IS the user_id (references auth.users.id)
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile) {
        throw new Error('User profile not found');
      }

      // Build query for services
      let query = supabase
        .from('industry_services')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('is_active', true)
        .order('name');

      // Apply filters
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      // Transform to Service interface
      const transformedServices: Service[] = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price,
        unit: 'hora', // Default unit, can be enhanced
        category_id: item.category_id?.toString(),
        cost_center_id: '1', // Default, can be enhanced
        is_active: item.is_active,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setServices(transformedServices);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err : new Error('Failed to load services'));
      setServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = () => loadServices();

  return {
    services,
    isLoading,
    error,
    refetch,
  };
}